import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useBackofficeUser,
  useUpdateUserRole,
  useSetUserActive,
  useGeneratePasswordReset,
} from '@/hooks/backoffice/useBackofficeUsers'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { CopyableLink } from '@/components/CopyableLink'
import type { UserRole, PasswordResetTokenResult } from '@/types/backoffice'

const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Admin',
  OPERATOR: 'Operador',
  FINANCIAL: 'Financeiro',
}

export function BackofficeUserDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useBackofficeUser(id ?? '')
  const updateRole = useUpdateUserRole(id ?? '')
  const setActive = useSetUserActive()
  const genReset = useGeneratePasswordReset()
  const [resetResult, setResetResult] = useState<PasswordResetTokenResult | null>(null)

  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>
  }

  const handleRoleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value as UserRole
    if (role === data.role) return
    if (!confirm(`Alterar role para ${ROLE_LABEL[role]}?`)) return
    await updateRole.mutateAsync(role)
  }

  const handleToggle = async () => {
    if (!confirm(data.active ? 'Desativar usuário?' : 'Reativar usuário?')) return
    await setActive.mutateAsync({ id: data.id, active: !data.active })
  }

  const handleGenerateReset = async () => {
    const res = await genReset.mutateAsync(data.id)
    setResetResult(res)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{data.name}</h1>
            <StatusBadge active={data.active} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{data.email}</p>
          <p className="text-sm text-muted-foreground">
            Tenant:{' '}
            <button
              onClick={() => navigate(`/backoffice/tenants/${data.tenant.id}`)}
              className="font-medium text-foreground underline-offset-2 hover:underline"
            >
              {data.tenant.name}
            </button>{' '}
            ({data.tenant.cnpj}){!data.tenant.active && ' — tenant inativo'}
          </p>
        </div>
        <Button variant={data.active ? 'destructive' : 'default'} onClick={handleToggle}>
          {data.active ? 'Desativar' : 'Reativar'}
        </Button>
      </div>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">Role</h2>
        <select
          value={data.role}
          onChange={handleRoleChange}
          className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="ADMIN">Admin</option>
          <option value="OPERATOR">Operador</option>
          <option value="FINANCIAL">Financeiro</option>
        </select>
      </section>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Reset de senha</h2>
          <Button size="sm" onClick={handleGenerateReset} disabled={genReset.isPending}>
            {genReset.isPending ? 'Gerando…' : 'Gerar link de reset'}
          </Button>
        </div>
        {resetResult && (
          <CopyableLink
            url={resetResult.resetUrl}
            hint={`Expira em ${new Date(resetResult.expiresAt).toLocaleString('pt-BR')}. Envie o link diretamente ao usuário.`}
          />
        )}
      </section>
    </div>
  )
}
